import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import CrudForm from "../../components/common/crud/CrudForm";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import alertService from "../../utils/alertService";

const bankFormSchema = [
  {
    name: "bankName",
    label: "Bank Name",
    type: "text",
    placeholder: "e.g. State Bank of India",
    validation: {
      required: true,
      minLength: 2,
      maxLength: 100,
    },
  },
  {
    name: "accountNumber",
    label: "Account Number",
    type: "text",
    placeholder: "e.g. 123456789012",
    validation: {
      required: true,
      minLength: 2,
      maxLength: 50,
    },
  },
  {
    name: "ifscCode",
    label: "IFSC Code",
    type: "text",
    placeholder: "e.g. SBIN0001234",
    validation: {
      maxLength: 20,
    },
  },
  {
    name: "utrReferenceNumber",
    label: "UTR / Reference Number",
    type: "text",
    placeholder: "e.g. UTR987654321",
    validation: {
      maxLength: 50,
    },
  },
  {
    name: "isActive",
    label: "Active",
    type: "checkbox",
  },
];

function BankModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  const [editValues, setEditValues] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);

  const isCreatePage = location.pathname.endsWith("/new") || location.pathname.endsWith("/create");
  const isEditPage = Boolean(id) && (location.pathname.endsWith("/edit") || location.pathname.includes(`/${id}`));
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/bank-master")) return "/settings-master/bank-master";
    if (location.pathname.includes("/settings-master/bank")) return "/settings-master/bank";
    if (location.pathname.includes("/bank-master")) return "/bank-master";
    if (location.pathname.includes("/master/bank")) return "/master/bank";
    return "/bank";
  }, [location.pathname]);

  const endpoints = useMemo(
    () => ({
      list: "/bank",
      create: "/bank",
      edit: (id) => `/bank/${id}`,
      update: (id) => `/bank/${id}`,
      delete: (id) => `/bank/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "bankId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "bankName", order: "ASC" },
  });

  const bankTableColumns = useMemo(
    () => [
      { key: "bankName", label: "Bank Name", sortable: true },
      { key: "accountNumber", label: "Account Number", sortable: true },
      {
        key: "ifscCode",
        label: "IFSC Code",
        render: (item) => item?.ifscCode || "-",
      },
      {
        key: "utrReferenceNumber",
        label: "UTR / Ref No.",
        render: (item) => item?.utrReferenceNumber || "-",
      },
      {
        key: "isActive",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (!isEditPage || !id) {
      setEditValues(null);
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditBank = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/bank/${id}`);
        const payload = response?.data || {};
        const bank = payload?.data || payload || {};
        if (!isMounted) return;
        setEditValues(bank);
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditBank();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  const saveBank = async (values) => {
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        await apiClient.patch(`/bank/${id}`, values);
        alertService.success("Record updated successfully.");
      } else {
        await apiClient.post("/bank", values);
        alertService.success("Record created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => {
    const bankId = item?.bankId || item?.id;
    if (bankId) {
      navigate(`${basePath}/${encrypt(bankId)}/edit`);
    }
  };

  if (isFormPage) {
    const notFound = formMode === "edit" && !isLoadingEdit && !editValues && !formError;

    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Record not found.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <CrudForm
              mode={formMode}
              schema={bankFormSchema}
              initialValues={formMode === "edit" ? editValues || {} : { isActive: true }}
              isSubmitting={isSubmitting || isLoadingEdit}
              onSubmit={saveBank}
              onCancel={() => navigate(basePath)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={bankTableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search by bank name or account number"
        tableScrollHeight="52vh"
        toolbarAction={<Button onClick={openCreate}>Add Bank</Button>}
      />
    </div>
  );
}

export default BankModulePage;
